/**
 * Route configuration for breadcrumbs.
 *
 * Each route pattern maps to breadcrumb metadata including:
 * - label: The display text for the breadcrumb segment
 * - resolver: Optional entity type to resolve dynamic names (e.g., "team" -> team.name)
 *
 * Patterns use :param syntax to match dynamic segments.
 */
export var EntityResolver;
(function (EntityResolver) {
    EntityResolver["Team"] = "team";
    EntityResolver["League"] = "league";
    EntityResolver["Venue"] = "venue";
    EntityResolver["Player"] = "player";
    EntityResolver["Season"] = "season";
    EntityResolver["Division"] = "division";
    EntityResolver["IntraSquadMatch"] = "intraSquadMatch";
    EntityResolver["ExternalMatch"] = "externalMatch";
    EntityResolver["IntraSquadTournament"] = "intraSquadTournament";
    EntityResolver["IntraSquadTournamentMatch"] = "intraSquadTournamentMatch";
    EntityResolver["Transfer"] = "transfer";
    EntityResolver["LeagueMatch"] = "leagueMatch";
})(EntityResolver || (EntityResolver = {}));
/**
 * Route path constants for use across the application.
 * Single source of truth for all route paths.
 */
export const ROUTES = {
    // Home
    HOME: "/",
    LOGIN: "/login",
    // Teams
    TEAMS: "/teams",
    TEAMS_CREATE: "/teams/create",
    TEAM_DETAIL: "/teams/:id",
    TEAM_EDIT: "/teams/:id/edit",
    TEAM_STATS: "/teams/:id/stats",
    TEAM_VENUES: "/teams/:id/venues",
    TEAM_VENUES_ADD: "/teams/:id/venues/add",
    TEAM_PREFERENCES_EDIT: "/teams/:id/preferences/edit",
    TEAM_ADD_PLAYER: "/teams/:id/add-player",
    TEAM_ADD_PLAYER_CREATE: "/teams/:id/add-player/create",
    TEAM_ADD_PLAYER_EXISTING: "/teams/:id/add-player/existing",
    TEAM_FORMATION: "/teams/:id/formation",
    TEAM_ASSISTANT_MANAGERS: "/teams/:id/assistant-managers",
    TEAM_SQUAD: "/teams/:id/squad",
    TEAM_SQUAD_EDIT: "/teams/:id/squad/:playerId/edit",
    TEAM_SQUAD_MERGE: "/teams/:id/squad/merge",
    TEAM_INTRA_SQUAD_MATCH_CREATE: "/teams/:id/intra-squad-match/create",
    TEAM_EXTERNAL_MATCH_CREATE: "/teams/:id/external-match/create",
    TEAM_INTRA_SQUAD_TOURNAMENTS_CREATE: "/teams/:id/intra-squad-tournaments/create",
    TEAM_INVITATIONS: "/teams/:id/invitations",
    TEAM_INVITATIONS_SEND: "/teams/:id/invitations/send",
    // Invitations
    MY_INVITATIONS: "/invitations",
    INVITATION_DETAIL: "/invitations/:id",
    JOIN_TEAM_BY_LINK: "/join/team/:token",
    JOIN_TEAM_BY_INVITATION: "/join/invitation/:token",
    POST_JOIN_PROFILE: "/welcome/profile/:teamId",
    // League-Team Invitations
    LEAGUE_TEAM_INVITATION_DETAIL: "/league-invitations/:id",
    // Team Ownership Invitations
    MY_OWNERSHIP_INVITATIONS: "/ownership-invitations",
    OWNERSHIP_INVITATION_DETAIL: "/ownership-invitations/:id",
    TEAM_OWNERSHIP_INVITATIONS: "/teams/:id/ownership-invitations",
    TEAM_OWNERSHIP_INVITATIONS_SEND: "/teams/:id/ownership-invitations/send",
    ACCEPT_OWNERSHIP_BY_TOKEN: "/accept/ownership/:token",
    // Venues
    VENUES: "/venues",
    VENUES_CREATE: "/venues/create",
    VENUE_DETAIL: "/venues/:id",
    VENUE_PITCH_CREATE: "/venues/:venueId/pitches/create",
    // Players
    PLAYER_DETAIL: "/players/:id",
    PLAYER_EDIT: "/players/:id/edit",
    // Leagues
    LEAGUES: "/leagues",
    LEAGUES_CREATE: "/leagues/create",
    LEAGUE_DETAIL: "/leagues/:id",
    LEAGUE_EDIT: "/leagues/:id/edit",
    LEAGUE_SEASONS_CREATE: "/leagues/:id/seasons/create",
    LEAGUE_VENUES: "/leagues/:id/venues",
    LEAGUE_VENUES_ADD: "/leagues/:id/venues/add",
    LEAGUE_TEAMS_ADD: "/leagues/:id/teams/add",
    LEAGUE_TEAMS_ADD_EXISTING: "/leagues/:id/teams/add-existing",
    LEAGUE_TEAMS_CREATE: "/leagues/:id/teams/create",
    LEAGUE_TEAMS_MANAGE: "/leagues/:id/teams/manage",
    LEAGUE_TEAMS: "/leagues/:id/teams",
    LEAGUE_INVITATIONS: "/leagues/:id/invitations",
    LEAGUE_INVITATIONS_SEND: "/leagues/:id/invitations/send",
    // Seasons
    SEASON_DETAIL: "/leagues/:leagueId/seasons/:id",
    SEASON_EDIT: "/leagues/:leagueId/seasons/:id/edit",
    SEASON_DIVISIONS_CREATE: "/leagues/:leagueId/seasons/:seasonId/divisions/create",
    // Divisions
    DIVISION_EDIT: "/leagues/:leagueId/seasons/:seasonId/divisions/:id/edit",
    // Matches (nested under teams for breadcrumbs)
    INTRA_SQUAD_MATCH_DETAIL: "/teams/:teamId/intra-squad-matches/:id",
    INTRA_SQUAD_MATCH_UPDATE: "/teams/:teamId/intra-squad-matches/:id/update",
    INTRA_SQUAD_MATCH_STATS_UPDATE: "/teams/:teamId/intra-squad-matches/:id/stats/update",
    EXTERNAL_MATCH_DETAIL: "/teams/:teamId/external-matches/:id",
    EXTERNAL_MATCH_UPDATE: "/teams/:teamId/external-matches/:id/update",
    EXTERNAL_MATCH_STATS_UPDATE: "/teams/:teamId/external-matches/:id/stats/update",
    INTRA_SQUAD_TOURNAMENT_MATCH_STATS_UPDATE: "/teams/:teamId/intra-squad-tournament-matches/:id/stats/update",
    LEAGUE_MATCH_CREATE: "/leagues/:leagueId/seasons/:seasonId/matches/create",
    LEAGUE_MATCH_BULK_CREATE: "/leagues/:leagueId/seasons/:seasonId/matches/bulk-create",
    LEAGUE_MATCH_BULK_RESULTS: "/leagues/:leagueId/seasons/:seasonId/gameweeks/:round/results",
    LEAGUE_MATCH_DETAIL: "/leagues/:leagueId/seasons/:seasonId/matches/:id",
    LEAGUE_MATCH_UPDATE: "/leagues/:leagueId/seasons/:seasonId/matches/:id/update",
    LEAGUE_MATCH_STATS_UPDATE: "/leagues/:leagueId/seasons/:seasonId/matches/:id/stats/update",
    // Tournaments (nested under teams for breadcrumbs)
    INTRA_SQUAD_TOURNAMENT_DETAIL: "/teams/:teamId/intra-squad-tournaments/:id",
    // Transfers
    TRANSFER_DETAIL: "/transfers/:id",
    // Search
    SEARCH: "/search",
    SEARCH_PLAYERS: "/search/players",
    SEARCH_TEAMS: "/search/teams",
    // Account & Settings
    ACCOUNT: "/account",
    PRIVACY_SETTINGS: "/privacy-settings",
    NOTIFICATIONS: "/notifications",
    // External Users
    EXTERNAL_USER_UPDATE: "/external-users/:id/update",
    // Public pages
    ABOUT_US: "/about-us",
    PRIVACY_POLICY: "/privacy-policy",
    TERMS_OF_SERVICE: "/terms-of-service",
    ADDING_PLAYERS: "/adding-players",
};
/**
 * Maps route patterns to their breadcrumb configuration.
 * TypeScript enforces that every route in ROUTES has a config entry.
 */
export const routeConfig = {
    // Home
    [ROUTES.HOME]: { label: "Home" },
    [ROUTES.LOGIN]: { label: "Login" },
    // Teams
    [ROUTES.TEAMS]: { label: "Teams" },
    [ROUTES.TEAMS_CREATE]: { label: "Create Team" },
    [ROUTES.TEAM_DETAIL]: { label: "Team", resolver: EntityResolver.Team },
    [ROUTES.TEAM_EDIT]: { label: "Edit" },
    [ROUTES.TEAM_STATS]: { label: "Stats" },
    [ROUTES.TEAM_VENUES]: { label: "Venues" },
    [ROUTES.TEAM_VENUES_ADD]: { label: "Add Venue" },
    [ROUTES.TEAM_PREFERENCES_EDIT]: { label: "Edit Preferences" },
    [ROUTES.TEAM_ADD_PLAYER]: { label: "Add Player" },
    [ROUTES.TEAM_ADD_PLAYER_CREATE]: { label: "Create Player" },
    [ROUTES.TEAM_ADD_PLAYER_EXISTING]: { label: "Add Existing Players" },
    [ROUTES.TEAM_FORMATION]: { label: "Formation" },
    [ROUTES.TEAM_ASSISTANT_MANAGERS]: { label: "Assistant Managers" },
    [ROUTES.TEAM_SQUAD]: { label: "Squad" },
    [ROUTES.TEAM_SQUAD_EDIT]: { label: "Edit Membership" },
    [ROUTES.TEAM_SQUAD_MERGE]: { label: "Merge Players" },
    [ROUTES.TEAM_INTRA_SQUAD_MATCH_CREATE]: { label: "Create Intra-Squad Match" },
    [ROUTES.TEAM_EXTERNAL_MATCH_CREATE]: { label: "Create External Match" },
    [ROUTES.TEAM_INTRA_SQUAD_TOURNAMENTS_CREATE]: { label: "Create Tournament" },
    [ROUTES.TEAM_INVITATIONS]: { label: "Invitations" },
    [ROUTES.TEAM_INVITATIONS_SEND]: { label: "Send Invitation" },
    // Venues
    [ROUTES.VENUES]: { label: "Venues" },
    [ROUTES.VENUES_CREATE]: { label: "Create Venue" },
    [ROUTES.VENUE_DETAIL]: { label: "Venue", resolver: EntityResolver.Venue },
    [ROUTES.VENUE_PITCH_CREATE]: { label: "Add Pitch" },
    // Players
    [ROUTES.PLAYER_DETAIL]: { label: "Player", resolver: EntityResolver.Player },
    [ROUTES.PLAYER_EDIT]: { label: "Edit" },
    // External Users
    [ROUTES.EXTERNAL_USER_UPDATE]: { label: "Update External User" },
    // Matches
    [ROUTES.INTRA_SQUAD_MATCH_DETAIL]: {
        label: "Intra-Squad Match",
        resolver: EntityResolver.IntraSquadMatch,
    },
    [ROUTES.INTRA_SQUAD_MATCH_UPDATE]: { label: "Update" },
    [ROUTES.INTRA_SQUAD_MATCH_STATS_UPDATE]: { label: "Update Stats" },
    [ROUTES.EXTERNAL_MATCH_DETAIL]: {
        label: "External Match",
        resolver: EntityResolver.ExternalMatch,
    },
    [ROUTES.EXTERNAL_MATCH_UPDATE]: { label: "Update" },
    [ROUTES.EXTERNAL_MATCH_STATS_UPDATE]: { label: "Update Stats" },
    // Tournaments
    [ROUTES.INTRA_SQUAD_TOURNAMENT_DETAIL]: {
        label: "Tournament",
        resolver: EntityResolver.IntraSquadTournament,
    },
    [ROUTES.INTRA_SQUAD_TOURNAMENT_MATCH_STATS_UPDATE]: {
        label: "Update Tournament Match Stats",
    },
    [ROUTES.LEAGUE_MATCH_CREATE]: {
        label: "Create Match",
    },
    [ROUTES.LEAGUE_MATCH_BULK_CREATE]: {
        label: "Add Gameweek",
    },
    [ROUTES.LEAGUE_MATCH_BULK_RESULTS]: {
        label: "Enter Results",
    },
    [ROUTES.LEAGUE_MATCH_DETAIL]: {
        label: "League Match",
        resolver: EntityResolver.LeagueMatch,
    },
    [ROUTES.LEAGUE_MATCH_UPDATE]: {
        label: "Update",
    },
    [ROUTES.LEAGUE_MATCH_STATS_UPDATE]: {
        label: "Update Stats",
    },
    // Leagues
    [ROUTES.LEAGUES]: { label: "Leagues" },
    [ROUTES.LEAGUES_CREATE]: { label: "Create League" },
    [ROUTES.LEAGUE_DETAIL]: { label: "League", resolver: EntityResolver.League },
    [ROUTES.LEAGUE_EDIT]: { label: "Edit" },
    [ROUTES.LEAGUE_VENUES]: { label: "Venues" },
    [ROUTES.LEAGUE_VENUES_ADD]: { label: "Add Venue" },
    [ROUTES.LEAGUE_TEAMS_ADD]: { label: "Add Team" },
    [ROUTES.LEAGUE_TEAMS_ADD_EXISTING]: { label: "Add Existing Teams" },
    [ROUTES.LEAGUE_TEAMS_CREATE]: { label: "Create Team" },
    [ROUTES.LEAGUE_TEAMS_MANAGE]: { label: "Manage Teams" },
    [ROUTES.LEAGUE_TEAMS]: { label: "Teams" },
    [ROUTES.LEAGUE_INVITATIONS]: { label: "Invitations" },
    [ROUTES.LEAGUE_INVITATIONS_SEND]: { label: "Invite Team" },
    [ROUTES.LEAGUE_SEASONS_CREATE]: { label: "Create Season" },
    // Seasons
    [ROUTES.SEASON_DETAIL]: { label: "Season", resolver: EntityResolver.Season },
    [ROUTES.SEASON_EDIT]: { label: "Edit" },
    [ROUTES.SEASON_DIVISIONS_CREATE]: { label: "Create Division" },
    // Divisions
    [ROUTES.DIVISION_EDIT]: {
        label: "Edit Division",
        resolver: EntityResolver.Division,
    },
    // Transfers
    [ROUTES.TRANSFER_DETAIL]: {
        label: "Transfer Request",
        resolver: EntityResolver.Transfer,
    },
    // Invitations
    [ROUTES.MY_INVITATIONS]: { label: "Invitations" },
    [ROUTES.INVITATION_DETAIL]: { label: "Invitation" },
    [ROUTES.JOIN_TEAM_BY_LINK]: { label: "Join Team" },
    [ROUTES.JOIN_TEAM_BY_INVITATION]: { label: "Accept Invitation" },
    [ROUTES.POST_JOIN_PROFILE]: { label: "Set Up Profile" },
    // League-Team Invitations
    [ROUTES.LEAGUE_TEAM_INVITATION_DETAIL]: { label: "League Invitation" },
    // Team Ownership Invitations
    [ROUTES.MY_OWNERSHIP_INVITATIONS]: { label: "Ownership Invitations" },
    [ROUTES.OWNERSHIP_INVITATION_DETAIL]: { label: "Ownership Invitation" },
    [ROUTES.TEAM_OWNERSHIP_INVITATIONS]: { label: "Ownership Invitations" },
    [ROUTES.TEAM_OWNERSHIP_INVITATIONS_SEND]: { label: "Transfer Ownership" },
    [ROUTES.ACCEPT_OWNERSHIP_BY_TOKEN]: { label: "Accept Ownership" },
    // User Account
    [ROUTES.ACCOUNT]: { label: "Account" },
    [ROUTES.PRIVACY_SETTINGS]: { label: "Privacy Settings" },
    [ROUTES.NOTIFICATIONS]: { label: "Notifications" },
    // Search
    [ROUTES.SEARCH]: { label: "Search" },
    [ROUTES.SEARCH_PLAYERS]: { label: "Player Results" },
    [ROUTES.SEARCH_TEAMS]: { label: "Team Results" },
    // Static Pages
    [ROUTES.ABOUT_US]: { label: "About Us" },
    [ROUTES.PRIVACY_POLICY]: { label: "Privacy Policy" },
    [ROUTES.TERMS_OF_SERVICE]: { label: "Terms of Service" },
    [ROUTES.ADDING_PLAYERS]: { label: "Adding Players" },
};
/**
 * Extracts all route patterns from the config.
 * Used for testing to ensure all App.tsx routes are covered.
 */
export const getAllRoutePatterns = () => Object.keys(routeConfig);
